import React from 'react';
import { css } from 'emotion';
import type { Feature } from '@fulcrumapp/fulcrum-core';
import {
  AudioItemValue,
} from '@fulcrumapp/fulcrum-core';
import hooks from '../models/hooks';
import { shouldUseEsriMaps } from '../../sketch-editor';
import type { App } from '../../hooks/use-app';
import { AppContext } from '../../hooks/use-app';
import { cssInJS } from '../../utils/styles';
import MediaCaptionView from './media-caption-view';
import MediaContentView from './media-content-view';

import Track from './track';
import TrackPlayer from './track-player';
import type { DisplayableItem, Metadata } from './display/media-displayer';

const PROCESSING_POLLING_FREQUENCY = 10000;

export type MediaViewerProps = {
  feature?: Feature,
  // Info about the media to show. Sometimes this is available in parent components so we can skip loading it via the initialItem
  metadata?: Object,
  track?: Object,
  mediaValueItems: Array<DisplayableItem>,
  mapVisible?: boolean,
  initialItem: DisplayableItem,
  onClose?: () => void,
  time?: string,
  onGalleryClick?: () => void,
};

type MediaViewerState = {
  wantsMapVisible: boolean,
  activeItem: DisplayableItem,
  activeMetadata: Object | undefined,
  activeTrack?: Track,
  activeTrackPlayer: TrackPlayer | null,
  currentTime: number,
  seekTime: number,
  activeTrackPoint?: any,
};
export default class MediaViewer extends React.Component<MediaViewerProps, MediaViewerState> {
  static readonly contextType = AppContext;

  static defaultPropTypes = {
    mapVisible: true,
  };

  static styles: any;

  _checkProcessingTimer: NodeJS.Timeout | undefined = undefined;

  _unmounted = false;

  // Tracks which item is *currently intended* to be active, set synchronously whenever we switch.
  // Used (instead of `this.state.activeItem`) to guard against stale responses, since `setState` is
  // asynchronous and a response could otherwise resolve before the state update has flushed.
  _activeItem: DisplayableItem;

  // Monotonically increasing counter, bumped every time we switch items (see `changeItem`).
  // Comparing an item reference alone isn't enough to reject stale responses: in an A -> B -> A
  // sequence, two requests for A can be in flight (the original mount/first-visit request, and the
  // one issued when switching back to A) and both would pass an item-identity check. Each load
  // captures the token at request time and `updateActiveMedia` only applies a response whose token
  // still matches the latest one, so an older in-flight request for the same item can't clobber a
  // newer one's result.
  _requestToken = 0;

  // Session-scoped cache of previously loaded metadata, keyed by mediaID, so switching back to an
  // already-viewed item doesn't re-fetch it. Items still processing (`stored: false`) are never
  // served from cache so they keep getting re-checked until they finish.
  _metadataCache = new Map<string, { metadata: Metadata, track: Track | null, stored: boolean }>();

  constructor(props: MediaViewerProps, context: App) {
    super(props, context);

    this._activeItem = props.initialItem;

    if (globalThis.URLSearchParams) {
      const urlParams = new URLSearchParams(globalThis.location.search);
      const showObjects = urlParams.get('objects');

      if (showObjects != null) {
        this.showPhotoObjects = showObjects === '1';
      }
    }

    this.state = {
      wantsMapVisible: this.context.preferences.isSectionExpanded('media-map') && props.mapVisible,
      activeItem: props.initialItem,
      activeMetadata: props.metadata,
      activeTrack: props.track ? new Track(props.track) : undefined,
      activeTrackPlayer: null,
      currentTime: this.parseTime(props.time) || 0,
      seekTime: 0,
    };
  }

  get showPhotoObjects() {
    const value = localStorage.getItem('showPhotoObjects');

    return value === null ? false : value === 'true';
  }

  set showPhotoObjects(value) {
    localStorage.setItem('showPhotoObjects', value ? 'true' : 'false');
  }

  componentDidMount() {
    const initialItem = this.state.activeItem;
    const requestToken = this._requestToken;

    if (this.state.activeMetadata) {
      this.updateActiveMedia(
        null,
        this.state.activeMetadata as any,
        this.state.activeTrack as any,
        initialItem,
        requestToken,
      );
    } else {
      initialItem.loadMetadata(
        (error, media, track) => this.updateActiveMedia(error, media, track, initialItem, requestToken),
      );
    }

    globalThis.__currentMediaViewer = this;

    hooks.emit('load-media-viewer', this);
  }

  componentWillUnmount() {
    this._unmounted = true;

    clearTimeout(this._checkProcessingTimer);

    hooks.emit('unload-media-viewer', this);

    globalThis.__currentMediaViewer = null;
  }

  render() {
    if (!globalThis.isGoogleMapsLoaded) {
      console.warn('Waiting for Google Maps to load');
    }

    const { styles } = MediaViewer;

    const contentClass = this.shouldMapBeVisible && this.state.activeItem.supportsMapView ? styles.mediaWithMap
      : styles.mediaWithoutMap;

    return (
      <div className={css(styles.container)}>
        <MediaCaptionView
          className={css(styles.caption)}
          displayableItem={this.state.activeItem}
          metadata={this.state.activeMetadata}
          onMapToggle={this.handleMapToggle}
          onClose={this.props.onClose}
          track={this.state.activeTrack}
          mapVisible={this.shouldMapBeVisible}
          onGalleryClick={this.props.onGalleryClick}
        />

        <MediaContentView
          className={css(contentClass)}
          mediaValueItems={this.props.mediaValueItems}
          item={this.state.activeItem}
          index={this.currentIndex}
          metadata={this.state.activeMetadata}
          onPrevious={this.handlePrevious}
          onNext={this.handleNext}
          onMediaChange={this.handleMediaChange}
          initialTime={this.state.currentTime}
          seekTime={this.state.seekTime}
          onTimeUpdate={this.handleTimeUpdate}
          showLabels={this.showPhotoObjects}
          showText={this.showPhotoObjects}
          onClose={this.props.onClose || (() => {})}
        />

        {this.renderMapView()}
        {this.renderMetadataView()}
      </div>
    );
  }

  renderMapView() {
    if (!this.canMapBeVisible || !this.state.activeItem.supportsMapView) {
      return null;
    }

    const { styles } = MediaViewer;

    const mapClass = this.shouldMapBeVisible ? styles.map
      : styles.noMap;
    const appContext = this.context as App;

    const useEsriMap = shouldUseEsriMaps(
      appContext.globals,
      (this.props.feature as any)?.form,
    );

    return this.state.activeItem.displayer.mapView(
      this.state.activeItem.item,
      this.state.activeMetadata as Metadata,
      css(mapClass),
      this.props.feature,
      this.shouldMapBeVisible,
      {
        activeTrack: this.state.activeTrack,
        activeTrackPoint: this.state.activeTrackPoint,
        handleTrackClick: this.handleTrackClick,
      },
      useEsriMap,
    );
  }

  renderMetadataView() {
    const { styles } = MediaViewer;

    return this.state.activeItem.displayer.metadata(
      this.state.activeItem.item,
      this.state.activeMetadata as Metadata,
      css(styles.metadata),
      this.props.feature,
      {
        activeTrack: this.state.activeTrack,
        activeTrackPoint: this.state.activeTrackPoint,
      },
    );
  }

  get currentIndex() {
    if (this.isStandlone) {
      return -1;
    }

    for (let index = 0; index < this.props.mediaValueItems.length; ++index) {
      if (this.props.mediaValueItems[index].item === this.state.activeItem.item) {
        return index;
      }
    }
    return -1;
  }

  get isLast() {
    if (this.isStandlone) {
      return true;
    }
    return this.currentIndex === this.props.mediaValueItems.length - 1;
  }

  get isFirst() {
    if (this.isStandlone) {
      return true;
    }
    return this.currentIndex === 0;
  }

  handlePrevious = () => {
    const index = Math.max(0, this.currentIndex - 1);

    const item = this.props.mediaValueItems[index];

    this.changeItem(item);
  };

  handleNext = () => {
    const index = Math.min(this.props.mediaValueItems.length - 1, this.currentIndex + 1);

    const item = this.props.mediaValueItems[index];

    this.changeItem(item);
  };

  handleTrackClick = (time) => {
    const player = this.state.activeTrackPlayer;

    if (player) {
      // @ts-ignore
      const seconds = (time - player.firstTimestamp) / 1000;

      this.setState({
        seekTime: seconds,
        currentTime: seconds,
        activeTrackPoint: player.point(seconds),
      });
    }
  };

  handleMediaChange = (item) => {
    this.changeItem(item);
  };

  handleMapToggle = () => {
    const wantsMapVisible = !this.state.wantsMapVisible;

    this.setState({ wantsMapVisible });

    this.context.preferences.setSectionExpanded('media-map', wantsMapVisible);
  };

  handleTimeUpdate = (time) => {
    const player = this.state.activeTrackPlayer;

    if (player) {
      const newState: Pick<MediaViewerState, 'activeTrackPoint' | 'currentTime'> = {
        activeTrackPoint: player.point(time),
        currentTime: this.state.activeItem.item instanceof AudioItemValue ? time : 0,
      };

      this.setState(newState);
    }
  };

  changeItem(item) {
    if (item !== this.state.activeItem) {
      this._activeItem = item;

      // Bump the token even on a cache hit (no fetch below) - it's what invalidates any
      // still-in-flight request left over from a previous visit to this same item.
      this._requestToken += 1;
      const requestToken = this._requestToken;

      // A pending re-check for the item we're switching away from would otherwise fire later and
      // issue a redundant fetch against whatever item happens to be active at that time.
      clearTimeout(this._checkProcessingTimer);
      this._checkProcessingTimer = undefined;

      const cached = this._metadataCache.get(item.item.mediaID);
      const useCache = !!cached?.stored;

      // Rebuild the track player from the cached track (mirroring updateActiveMedia below) rather
      // than leaving the previous item's player/point in place - otherwise handleTrackClick /
      // handleTimeUpdate and the map's highlighted point would compute positions against the wrong
      // track after switching back to a cached video/audio item.
      const cachedTrack = useCache ? cached.track : undefined;
      const cachedPlayer = cachedTrack ? new TrackPlayer(cachedTrack) : null;

      this.setState({
        activeItem: item,
        activeMetadata: useCache ? cached.metadata : undefined,
        activeTrack: cachedTrack ?? undefined,
        activeTrackPlayer: cachedPlayer,
        activeTrackPoint: cachedPlayer ? cachedPlayer.point(0) : undefined,
        currentTime: 0,
      });

      if (!useCache) {
        item.loadMetadata((error, media, track) => this.updateActiveMedia(error, media, track, item, requestToken));
      }
    }
  }

  parseTime(time) {
    if (time == null) {
      return 0;
    }

    const [hours, minutes, seconds] = time.split(':');

    const total = (+hours * 60 * 60)
                  + (+minutes * 60)
                  + +seconds;

    return total && !Number.isNaN(total) ? total : 0;
  }

  get isStandlone() {
    return this.props.mediaValueItems == null;
  }

  updateActiveMedia(
    error: Error | null,
    media: Metadata | null,
    track: Track | null,
    requestItem?: DisplayableItem,
    requestToken?: number,
  ) {
    if (this._unmounted) {
      return;
    }

    if (error) {
      console.error('Error loading media metadata', error);
      return;
    }

    // Ignore stale/out-of-order responses - without this, a slow response could resolve after a
    // newer request was made and briefly flash stale content over the current selection. Comparing
    // item identity alone isn't sufficient: in an A -> B -> A sequence, two requests for A can be
    // in flight at once (see `_requestToken`'s comment), and both would match `_activeItem`. The
    // token comparison rejects any response whose request has since been superseded, whether by a
    // switch to a different item or a second visit back to the same one. This must run *before*
    // the cache write below too - otherwise a stale response for A could still overwrite a newer,
    // already-cached result for A even though it's ignored for render purposes.
    if (requestToken !== undefined && requestToken !== this._requestToken) {
      return;
    }

    // Cache the response so navigating back to this item later in the same viewer session doesn't
    // require re-fetching it.
    if (requestItem && media) {
      this._metadataCache.set(requestItem.item.mediaID, { metadata: media, track, stored: !!media.stored });
    }

    const player = track ? new TrackPlayer(track) : null;
    const activeTrack = track;

    // We could just add this on to the metadata object as a property, but that's kinda
    // hacky. Let's leave it explicitly a separate entity here so it's very obvious it's *not* from
    // the same request. The metadata and track go together, but they require 2 separate requests
    // to fetch. Also, just adding it onto the object might cause very weird bugs later if the
    // metadata objects are cached and a track changes. We would have to remember to invalidate
    // 2 objects.

    const afterUpdate = () => {
      hooks.emit('load-media-metadata', this);
    };

    this.setState({
      activeMetadata: media || undefined,
      activeTrack: activeTrack || undefined,
      activeTrackPoint: player && player.point(this.state.currentTime),
      activeTrackPlayer: player,
    }, afterUpdate);

    clearTimeout(this._checkProcessingTimer);

    if (media && !media.stored) {
      const pollItem = requestItem || this._activeItem;
      // The guard above already confirmed this request's token is current, so re-use it for the
      // poll rather than reading `this._requestToken` again - a switch could otherwise happen
      // between now and when the timer fires, and we want the poll rejected in that case too.
      const pollToken = requestToken;

      this._checkProcessingTimer = setTimeout(() => {
        this._checkProcessingTimer = undefined;

        pollItem.loadMetadata((pollError, pollMedia, pollTrack) => {
          this.updateActiveMedia(pollError, pollMedia, pollTrack, pollItem, pollToken);
        });
      }, PROCESSING_POLLING_FREQUENCY);
    }
  }

  get shouldMapBeVisible() {
    return !!(this.state.wantsMapVisible && this.canMapBeVisible);
  }

  get canMapBeVisible() {
    // @ts-ignore
    return !!(this.state.activeMetadata && this.state.activeMetadata.stored);
  }
}

const styles = cssInJS({
  container: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
    minHeight: 300,
    lineHeight: 'normal',
    fontSize: '100%',
    // @ts-ignore
    '@phone': {
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch',
    },
  },

  caption: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    height: 55,
    // @ts-ignore
    '@phone': {
      position: 'relative',
      width: '100%',
      height: 'auto',
      paddingBottom: 14,
    },
  },

  mediaWithMap: {
    position: 'absolute',
    left: 0,
    top: 55,
    bottom: 0,
    width: '50%',
    // @ts-ignore
    '@phone': {
      position: 'relative',
      top: 0,
      left: 0,
      width: '100%',
    },
  },

  mediaWithoutMap: {
    position: 'absolute',
    left: 0,
    top: 55,
    bottom: 0,
    right: 300,
    // @ts-ignore
    '@phone': {
      position: 'relative',
      top: 0,
      left: 0,
      width: '100%',
      height: '45%',
    },
  },

  map: {
    position: 'absolute',
    left: '50%',
    right: 300,
    top: 55,
    bottom: 0,
    // @ts-ignore
    '@phone': {
      position: 'static',
      width: '100%',
      height: '45%',
    },
  },

  noMap: {
    position: 'absolute',
    left: '50%',
    right: 300,
    top: 55,
    bottom: 0,
    width: 0,
    height: 0,
    // @ts-ignore
    '@phone': {
      position: 'static',
      width: 0,
      height: 0,
    },
  },

  metadata: {
    position: 'absolute',
    right: 0,
    top: 55,
    bottom: 0,
    width: 300,
    overflowY: 'auto',
    zIndex: 100,
    // @ts-ignore
    '@phone': {
      position: 'static',
      width: '100%',
    },
  },
});

MediaViewer.styles = styles;
